/**
 * Component to display detailed information about a scope when hovered over.
 * Includes the linked Schedule block's date span when the scope belongs to one.
 *
 * @component
 * @param {Object} props - The component props.
 * @param {Object} props.scope - The scope object containing details to display.
 * @param {number} props.containerWidth - The width of the container for styling purposes.
 * @param {boolean} [props.weeklyContext=false] - Optional flag indicating if the component is used within a weekly context.
 * @returns {JSX.Element} The rendered component.
 */
import React from "react";
import { Z_INDEX, BACKGROUND_VARIANTS } from "../../../../config";

import {
  formatShortMonthDay,
  getDateInLocalTZFromDateStr,
  timeToString,
} from "../../../../utils/datetime";

import DetailsBoxView from "../workScopeEdit/DetailsBoxView";
import { getScopeBlockHighlightHex } from "./helpers";

/**
 * One-line summary of a scope's linked Schedule block, e.g.
 * "Schedule block: Jun 8 – Jun 12 (5 days)".
 *
 * @param {{ start_date: string, end_date: string, aspect_count: number }} block
 * @returns {string}
 */
function formatScheduleBlockSpan(block) {
  const start = formatShortMonthDay(getDateInLocalTZFromDateStr(block.start_date));
  const end = formatShortMonthDay(getDateInLocalTZFromDateStr(block.end_date));
  const days = `${block.aspect_count} ${block.aspect_count === 1 ? "day" : "days"}`;
  return `${start} – ${end} (${days})`;
}

const ScopeHoverDetails = ({
  scope,
  containerWidth,
  className,
  showScopeName = false,
  weeklyContext = false,
}) => {
  const scopeData =
    scope.concrete?.length > 0 ||
    scope.equipment?.length > 0 ||
    scope.subcontractor?.length > 0 ||
    scope.dumping?.length > 0 ||
    scope.worker_assignment?.length > 0 ||
    scope.notes?.length > 0 ||
    (scope.extra_data_sections?.length ?? 0) > 0;

  return (
    <div
      className={`${className} ${Z_INDEX.overlay} overflow-x-hidden text-gray-200 p-1 text-2xs`}
      style={{ width: containerWidth }}
    >
      {scope.schedule_block ? (
        // Gray chip with text in the card's block-highlight color so the
        // hover details visually tie back to the highlighted cards.
        <p
          className="mb-1 rounded bg-gray-200 font-white px-1 py-[2px]"
          style={{ backgroundColor: getScopeBlockHighlightHex(scope) + "90" }}
        >
          {formatScheduleBlockSpan(scope.schedule_block)}
        </p>
      ) : null}
      {scopeData ? (
        <>
          {showScopeName && (
            <div>
              <p className="font-bold text-lg pl-1">{scope.scope ? scope.scope?.title : "No Scope Name Set"}</p>
            </div>
          )}
          {(scope["custom_address"] || scope["custom_time"]) && (
            <div className="flex flex-col pb-1">
              {scope["custom_address"] && (
                <p className="pl-1 text-left">{scope["custom_address"]}</p>
              )}
              {scope["custom_time"] && (
                <p className="text-left pl-1">
                  {timeToString(scope["custom_time"])}
                </p>
              )}
            </div>
          )}
          <div className="flex flex-row flex-wrap pb-1">
            {scope.worker_assignment.map((worker, index) => (
              <div
                key={index}
                className="flex flex-row relative group/initials cursor-default text-xs"
              >
                <p
                  className={`${BACKGROUND_VARIANTS.dark.contrast} px-[4px] py-[2px] my-[2px] rounded-lg mx-[1px]`}
                >
                  {worker.first} {worker.last}
                </p>
              </div>
            ))}
          </div>
          <>
            {scope.concrete.length > 0 ? (
              <div className="font-bold ml-1 flex items-center">Details</div>
            ) : null}
            <div className="px-1 mb-1">
              {["concrete", "equipment", "subcontractor", "dumping"].map(
                (item, index) =>
                  scope[item].length > 0 && (
                    <DetailsBoxView
                      key={index}
                      data={scope[item]}
                      variant="dark"
                      title={weeklyContext ? true : false} // This option renders a component that expects to be inside weeklyContext
                    />
                  )
              )}
            </div>
          </>
          {scope.notes ? (
            <div>
              <p className="font-bold px-1">Notes</p>
              <p className="px-1 font-xs font-normal whitespace-normal overflow-x-hidden">
                {scope.notes}
              </p>
            </div>
          ) : null}
          {(scope.extra_data_sections ?? []).map((sec, idx) => {
            return (
              <div key={idx}>
                <p className="font-bold px-1">{sec.title || "Section"}</p>
                <p className="px-1 font-xs font-normal whitespace-normal overflow-x-hidden">
                  {sec.body}
                </p>
              </div>
            );
          })}
        </>
      ) : (
        <p className="p-1">No Scope Details Entered Yet</p>
      )}
    </div>
  );
};

export default ScopeHoverDetails;
